import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function SignagePlayer() {
  const [config, setConfig] = useState(null);
  const [content, setContent] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [loadedVideos, setLoadedVideos] = useState(new Set()); // Track loaded videos
  const [hasInteracted, setHasInteracted] = useState(false); // Track user interaction
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const nextVideoRef = useRef(null); // Preload next video

  // Get screen ID from URL
  const urlParams = new URLSearchParams(window.location.search);
  const screenId = urlParams.get('screen');

  // Enable audio after first interaction
  useEffect(() => {
    const enableAudio = () => {
      console.log('[PLAYER] User interaction detected - enabling audio');
      setHasInteracted(true);
      
      // Unmute current video if playing
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.muted = false;
      }
    };

    // Listen for any interaction
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('touchstart', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, []);

  useEffect(() => {
    if (!screenId) {
      setError('Missing screen parameter in URL');
      setLoading(false);
      return;
    }

    fetchConfig();
    setupWebSocket();

    // Update clock every second
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(clockInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [screenId]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/signage/screens/${screenId}/config`);
      const screenData = res.data.screen;
      const configData = res.data.config;
      const contentData = res.data.content || [];
      
      console.log('[PLAYER] Fetched config:', {
        screen: screenData,
        config: configData,
        contentCount: contentData.length,
        content: contentData
      });
      
      setConfig(configData);
      
      // Check if screen is active
      if (screenData.is_active === false) {
        console.log('[PLAYER] Screen is OFF');
        setContent([]);
        setLoading(false);
        return;
      }
      
      // Screen is ON - show selected content
      console.log('[PLAYER] Setting content:', contentData);
      setContent(contentData);
      setCurrentIndex(0);
      setLoading(false);
    } catch (err) {
      console.error('[PLAYER] Failed to fetch config:', err);
      setError('Failed to load configuration');
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    const ws = new WebSocket(`${WS_URL}/ws/signage/${screenId}`);

    ws.onopen = () => {
      console.log('[SIGNAGE] WebSocket connected');
      // Send heartbeat every 10 seconds
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 10000);

      ws.onclose = () => {
        clearInterval(heartbeat);
      };
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('[SIGNAGE] Received message:', message);

      if (message.type === 'config_update') {
        // Reload configuration
        fetchConfig();
      } else if (message.type === 'pong') {
        // Heartbeat response
      }
    };

    ws.onerror = (err) => {
      console.error('[SIGNAGE] WebSocket error:', err);
    };

    ws.onclose = () => {
      console.log('[SIGNAGE] WebSocket disconnected, reconnecting in 5s...');
      setTimeout(setupWebSocket, 5000);
    };

    wsRef.current = ws;
  };

  // Preload next item to eliminate delay
  useEffect(() => {
    if (!content.length || content.length < 2) return;
    
    const nextIndex = (currentIndex + 1) % content.length;
    const nextItem = content[nextIndex];
    
    if (nextItem && nextItem.type === 'video' && nextVideoRef.current) {
      // Aggressively preload next video with AUTO preload
      const nextVideoUrl = `${API}/signage/content/${nextItem.content_id}/download`;
      nextVideoRef.current.src = nextVideoUrl;
      nextVideoRef.current.preload = 'auto'; // Download as much as possible
      nextVideoRef.current.load();
      console.log('[PLAYER] Preloading next video aggressively:', nextItem.original_filename);
    }
  }, [currentIndex, content]);

  // Auto-advance logic - works for both videos and images
  useEffect(() => {
    console.log('[PLAYER] Auto-advance effect triggered', {
      hasConfig: !!config,
      contentLength: content.length,
      currentIndex,
      currentItem: content[currentIndex]
    });

    if (!config || !content.length) {
      console.log('[PLAYER] No config or content, skipping auto-advance');
      return;
    }

    const duration = (config.settings?.duration || 5) * 1000;
    const currentItem = content[currentIndex];

    console.log('[PLAYER] Current item:', {
      type: currentItem?.type,
      filename: currentItem?.original_filename,
      duration: `${duration}ms`,
      index: currentIndex,
      totalItems: content.length
    });

    // For images, auto-advance after duration
    if (currentItem && currentItem.type === 'image') {
      console.log(`[PLAYER] Setting image timer for ${duration}ms`);
      timerRef.current = setTimeout(() => {
        const nextIndex = (currentIndex + 1) % content.length;
        console.log(`[PLAYER] Image timer fired! Advancing from ${currentIndex} to ${nextIndex}`);
        setCurrentIndex(nextIndex);
      }, duration);

      return () => {
        if (timerRef.current) {
          console.log('[PLAYER] Clearing image timer');
          clearTimeout(timerRef.current);
        }
      };
    } else if (currentItem && currentItem.type === 'video') {
      console.log('[PLAYER] Current item is video - waiting for onEnded event');
    }
  }, [config, content, currentIndex]);

  // Handle video end - auto advance to next content
  const handleVideoEnd = () => {
    if (content.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % content.length);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-2xl font-light tracking-widest animate-pulse">
          ΦΟΡΤΩΣΗ...
        </div>
      </div>
    );
  }

  if (error || !screenId) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">⚠</div>
          <div className="text-white text-lg">{error || 'Invalid screen ID'}</div>
          <div className="text-gray-600 text-sm mt-2">
            URL format: /signage?screen=SCREEN_ID
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-xl">No configuration found</div>
      </div>
    );
  }

  const transition = config.settings?.transition || 'fade';
  const transitionDuration = transition === 'fade' ? 1000 : transition === 'slide' ? 700 : 0;
  
  // Transition classes
  const getTransitionClass = () => {
    if (transition === 'fade') {
      return 'animate-fadeIn';
    } else if (transition === 'slide') {
      return 'animate-slideIn';
    }
    return '';
  };

  // Fullscreen toggle function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.log('[PLAYER] Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Clock */}
      <div className="absolute top-4 left-4 z-50 text-white text-sm font-light tracking-wider">
        {currentTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
      </div>

      {/* Content Display - Mixed Videos & Images */}
      <div className="w-full h-full flex items-center justify-center relative">
        {/* Loading Spinner for Videos */}
        {isVideoLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
              <div className="text-sm tracking-wider">Φόρτωση βίντεο...</div>
            </div>
          </div>
        )}

        {content.length > 0 && content[currentIndex] ? (
          content[currentIndex].type === 'video' ? (
            // Video Display - Wait for FULL download before playing (no buffering)
            <video
              ref={videoRef}
              src={`${API}/signage/content/${content[currentIndex].content_id}/download`}
              muted={!hasInteracted}
              playsInline
              preload="auto"
              onLoadStart={() => {
                const videoId = content[currentIndex].content_id;
                // Show spinner for ALL loads to indicate downloading
                console.log('[PLAYER] Loading video:', videoId);
                setIsVideoLoading(true);
              }}
              onCanPlayThrough={() => {
                // Fires when ENTIRE video is downloaded - no buffering!
                const videoId = content[currentIndex].content_id;
                console.log('[PLAYER] Video FULLY downloaded - ready to play smoothly');
                
                setLoadedVideos(prev => new Set([...prev, videoId]));
                setIsVideoLoading(false);
                
                // Start playing now that entire video is ready
                if (videoRef.current) {
                  videoRef.current.play().catch(err => {
                    console.log('[PLAYER] Play attempt:', err);
                    // If unmuted autoplay fails, try muted
                    if (!hasInteracted) {
                      videoRef.current.muted = true;
                      videoRef.current.play();
                    }
                  });
                }
              }}
              onPlaying={() => {
                console.log('[PLAYER] Video is now playing');
                setIsVideoLoading(false);
                
                // Try to unmute if user has interacted
                if (hasInteracted && videoRef.current) {
                  videoRef.current.muted = false;
                }
              }}
              onEnded={handleVideoEnd}
              className="w-full h-full object-cover"
              style={{
                animation: transition === 'fade' 
                  ? 'fadeIn 0.3s ease-in' 
                  : transition === 'slide' 
                    ? 'slideIn 0.3s ease-out' 
                    : 'none'
              }}
            />
          ) : (
            // Image Display with faster transitions
            <div 
              key={content[currentIndex].content_id}
              className="w-full h-full"
              style={{
                animation: transition === 'fade' 
                  ? 'fadeIn 0.3s ease-in' 
                  : transition === 'slide' 
                    ? 'slideIn 0.3s ease-out' 
                    : 'none'
              }}
            >
              <img
                src={`${API}/signage/content/${content[currentIndex].content_id}/download`}
                alt="Signage"
                className="w-full h-full object-cover"
              />
            </div>
          )
        ) : (
          // No content - Show branding
          <div className="text-center">
            <div className="text-white text-3xl font-light tracking-widest mb-4">
              DIMAKAS
            </div>
            <div className="text-gray-600 text-sm tracking-wider">
              MODERN MALE IMAGE
            </div>
          </div>
        )}
      </div>

      {/* Hidden preload video for next item - aggressive preloading */}
      <video
        ref={nextVideoRef}
        className="hidden"
        preload="auto"
        muted
      />

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>

      {/* Fullscreen Button - Bottom Right Corner */}
      <button
        onClick={toggleFullscreen}
        className="group absolute bottom-4 right-4 z-50 p-3 rounded-lg transition-all duration-300 opacity-0 hover:opacity-100"
        style={{ 
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)'
        }}
        title="Toggle Fullscreen (F11)"
      >
        <svg 
          className="w-6 h-6 text-white transition-transform group-hover:scale-110" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" 
          />
        </svg>
      </button>
    </div>
  );
}

export default SignagePlayer;
